import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { DonationSummary, DonorDetail as Detail, Paginated } from '@foundation/shared';
import { DONATION_STATUS_LABEL_TH, DONOR_TYPE_LABEL_TH } from '@foundation/shared';
import { api } from '../lib/api';
import { formatDate, formatDateTime, formatSatang, maskNationalId } from '../lib/format';
import { ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

export default function DonorDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const [d, setD] = useState<Detail | null>(null);
  const [history, setHistory] = useState<DonationSummary[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [donor, donations] = await Promise.all([
        api<Detail>(`/donors/${id}`),
        api<Paginated<DonationSummary>>(`/donations?donorId=${id}&limit=100`),
      ]);
      setD(donor);
      setHistory(donations.data);
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !d) return <ErrorAlert error={error} />;
  if (!d) return <Loading />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{d.displayName}</h1>
          <p>
            <span className="mono">{d.donorCode}</span> ·{' '}
            {DONOR_TYPE_LABEL_TH[d.donorType] ?? d.donorType}
            {d.isAnonymous && ' · ไม่ประสงค์ออกนาม'}
          </p>
        </div>
        <Link className="btn secondary" to="/donors">
          ← กลับรายการ
        </Link>
      </div>

      <ErrorAlert error={error} />
      {notice && <div className="alert success">{notice}</div>}

      <div className="grid grid-4">
        <div className="card">
          <div className="stat-label">ยอดบริจาคสะสม</div>
          <div className="stat-value">{formatSatang(d.totalAmount)}</div>
        </div>
        <div className="card">
          <div className="stat-label">จำนวนครั้ง</div>
          <div className="stat-value">{d.donationCount}</div>
        </div>
        <div className="card">
          <div className="stat-label">บริจาคครั้งแรก</div>
          <div className="stat-value" style={{ fontSize: 18 }}>
            {formatDate(d.firstDonatedAt)}
          </div>
        </div>
        <div className="card">
          <div className="stat-label">บริจาคล่าสุด</div>
          <div className="stat-value" style={{ fontSize: 18 }}>
            {formatDate(d.lastDonatedAt)}
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h2>ข้อมูลติดต่อ</h2>
          <dl className="detail-list">
            <dt>เบอร์โทร</dt>
            <dd>{d.phone ?? '—'}</dd>
            <dt>อีเมล</dt>
            <dd>{d.email ?? '—'}</dd>
            <dt>ที่อยู่</dt>
            <dd>{d.addressLine ?? '—'}</dd>
            <dt>บัญชี LINE</dt>
            <dd>{d.lineLinked ? <span className="badge green">ผูกแล้ว</span> : '—'}</dd>
          </dl>
        </div>

        <div className="card">
          <h2>ข้อมูลใบอนุโมทนาบัตร</h2>
          <dl className="detail-list">
            <dt>ต้องการใบเสร็จ</dt>
            <dd>{d.wantsReceipt ? 'ใช่' : 'ไม่'}</dd>
            <dt>ชื่อบนใบเสร็จ</dt>
            <dd>{d.receiptName ?? d.displayName}</dd>
            <dt>เลขผู้เสียภาษี</dt>
            <dd className="mono">{maskNationalId(d.taxIdLast4)}</dd>
            <dt>ที่อยู่บนใบเสร็จ</dt>
            <dd>{d.receiptAddress ?? '—'}</dd>
          </dl>
        </div>
      </div>

      <div className="card">
        <h2>ประวัติการบริจาค ({history.length})</h2>
        {history.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            ยังไม่มีรายการบริจาค
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>เลขที่</th>
                  <th>ยอด</th>
                  <th>วันที่โอน</th>
                  <th>แคมเปญ</th>
                  <th>สถานะ</th>
                  <th>ใบเสร็จ</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="mono small">
                      <Link className="row-link" to={`/donations/${h.id}`}>
                        {h.donationNo}
                      </Link>
                    </td>
                    <td style={{ fontWeight: 600 }}>{formatSatang(h.amount)}</td>
                    <td className="small muted">{formatDateTime(h.donatedAt)}</td>
                    <td className="small">{h.campaignName ?? '—'}</td>
                    <td>
                      <span className={`badge ${h.status === 'verified' ? 'green' : 'amber'}`}>
                        {DONATION_STATUS_LABEL_TH[h.status] ?? h.status}
                      </span>
                    </td>
                    <td className="mono small">{h.receiptNo ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {d.note && (
        <div className="card">
          <h2>บันทึกเพิ่มเติม</h2>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }} className="small">
            {d.note}
          </p>
        </div>
      )}

      {me?.role === 'super_admin' && (
        <div className="card">
          <h2>คำขอลบข้อมูลตาม PDPA</h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            ระบบจะลบเฉพาะข้อมูลที่ระบุตัวตน (ชื่อ เบอร์โทร อีเมล ที่อยู่ เลขผู้เสียภาษี)
            แต่<strong>คงยอดเงิน ประวัติบริจาค และใบอนุโมทนาบัตรไว้</strong> เพราะเป็นเอกสารทางบัญชี
            ที่ต้องเก็บตามกฎหมายและต้องตอบผู้ตรวจสอบได้
          </p>
          <button
            className="danger"
            onClick={() => {
              const reason = prompt('เหตุผล (คำขอของเจ้าของข้อมูล) อย่างน้อย 5 ตัวอักษร');
              if (!reason || reason.length < 5) return;
              void (async () => {
                try {
                  await api(`/donors/${d.id}/anonymize`, { method: 'POST', body: { reason } });
                  setNotice('ลบข้อมูลระบุตัวตนแล้ว — ยอดเงินและใบเสร็จยังคงอยู่');
                  navigate('/donors');
                } catch (e) {
                  setError(e);
                }
              })();
            }}
          >
            ลบข้อมูลระบุตัวตน
          </button>
        </div>
      )}
    </>
  );
}
